/**
 * @file `LabAppBar` — the lab-specific 56px app bar (design §6/§7).
 * @module @labcraft/lab/ui/components/lab-app-bar
 *
 * Replaces the generic shared `Topbar` on every chromed lab screen. The
 * bar is lab-owned (not the shared `@labcraft/ui` `TopNav`, which admin
 * + www also consume) so the reader can carry a course label, a centered
 * chapter switcher, and a mirror toggle without those leaking into the
 * other hosts.
 *
 * Two layouts, chosen by {@link useReaderChrome}:
 *
 *   - **Reader** (context present): left = `lc>` mark + course label;
 *     center = the chapter switcher ("Ch X / Y" + prev/next arrows);
 *     right = `[report] [budget] [avatar]`. Terminal-layout (mirror) and
 *     theme are not bar shortcuts — both live in the Settings modal's
 *     controls.
 *   - **Minimal** (context `null`, any other chromed screen): left =
 *     `lc>` mark; right = `[report] [avatar]`.
 *
 * The "Report an issue" icon button (#1672) sits left of the budget meter
 * for a signed-in learner — quiet until hover, one click at the moment of
 * friction — and opens {@link ReportIssueModal}. It attaches the reader's
 * current context (published via {@link useReaderChrome}) when the learner
 * opts in; the lab daemon redacts + emails the report.
 *
 * Anonymous (no learner): the right cluster shows a plain "Sign in" link
 * to `/authorize` rather than the avatar — the shared `SignInPopover`
 * isn't reimplemented here; direct visits to `/authorize` serve the
 * full flow.
 *
 * The avatar dropdown is a lab-local {@link AvatarMenu} (the shared
 * `AccountMenu` doesn't carry the reader-specific items): account
 * header, Settings, Sign out. The free-tier {@link BudgetMeter} pill
 * (#941) sits to its left in the right cluster for a signed-in learner;
 * a paid learner shows no meter.
 */

import { useEffect, useRef, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { LabCraftLogo, ChevronRightIcon } from '@labcraft/ui'
import type { AccountLearner } from '../../client/account.ts'
import { useMe } from '../hooks/use_me.ts'
import { useSignoutMutation } from '../hooks/use_signout.ts'
import { useReportIssueMutation } from '../hooks/use_report_issue.ts'
import { useReaderChrome } from '../lib/reader_chrome.tsx'
import { resolveLandingForLearner } from '../lib/landing.ts'
import type { ReportRequest } from '../../shared/routes.ts'
import { BudgetMeter } from './budget_meter.tsx'
import { SettingsModal } from './settings_modal.tsx'
import { ReportIssueModal } from './report_issue_modal.tsx'
import styles from './lab_app_bar.module.css'

/**
 * The lab app bar. See the file header for the reader vs minimal
 * layout split and the anonymous fallback.
 *
 * @returns The app-bar header element.
 */
export function LabAppBar(): JSX.Element {
  const me = useMe()
  const learner = me.data?.learner ?? null
  const reader = useReaderChrome()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const reportMutation = useReportIssueMutation()

  const homeHref = learner
    ? resolveLandingForLearner(learner, '/')
    : '/authorize'

  return (
    <header className={styles.bar}>
      <div className={styles.left}>
        <a className={styles.brand} href={homeHref} aria-label="LabCraft home">
          <LabCraftLogo capH={20} />
        </a>
        {reader && (
          <span className={styles.courseLabel}>{reader.courseLabel}</span>
        )}
      </div>

      {reader && (
        <div className={styles.center}>
          <ChapterSwitcher
            label={reader.chapterLabel}
            prevHref={reader.chapterNav.prevHref}
            nextHref={reader.chapterNav.nextHref}
            nextLocked={reader.chapterNav.nextLocked}
          />
        </div>
      )}

      <div className={styles.right}>
        {learner ? (
          <>
            <button
              className={styles.iconBtn}
              type="button"
              aria-label="Report an issue"
              title="Report an issue"
              onClick={() => setReportOpen(true)}>
              <ReportIssueIcon />
            </button>
            <BudgetMeter />
            <AvatarMenu
              learner={learner}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          </>
        ) : (
          <a className={styles.signIn} href="/authorize">
            Sign in
          </a>
        )}
      </div>

      {settingsOpen && (
        <SettingsModal
          orientation={reader?.orientation ?? null}
          onOrientationChange={reader?.setOrientation ?? null}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {reportOpen && (
        <ReportIssueModal
          whereLabel={
            reader ? `${reader.courseLabel} · ${reader.chapterLabel}` : null
          }
          canAttachContext={reader?.collectReportContext != null}
          onSubmit={async draft => {
            const context =
              draft.attachContext && reader?.collectReportContext
                ? reader.collectReportContext()
                : undefined
            const req: ReportRequest = {
              category: draft.category,
              message: draft.message,
              attachContext: draft.attachContext,
              ...(context !== undefined && { context }),
              // Browser particulars ride on every report — a UI bug can
              // happen on any screen, so this isn't gated by the reader
              // attach-context toggle (#1672). The daemon redacts the url.
              client: collectClient(),
            }
            await reportMutation.mutateAsync(req)
          }}
          onClose={() => setReportOpen(false)}
        />
      )}
    </header>
  )
}

/**
 * Speech-bubble glyph for the report button — a 16px stroke icon in the
 * bar's `currentColor`, matching the icon-button vocabulary.
 *
 * @returns The inline SVG.
 */
function ReportIssueIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden>
      <path d="M2.5 3h11v7.5H8l-3 2.5v-2.5H2.5z" />
      <path d="M8 5.4v2.4" />
      <path d="M8 9.6v.01" />
    </svg>
  )
}

/**
 * Props for {@link ChapterSwitcher}.
 *
 * @property label - Centered position label, e.g. "Ch 3 / 6".
 * @property prevHref - Previous chapter SPA path, or `null` at the
 *   start of the course.
 * @property nextHref - Next chapter SPA path, or `null` at the end.
 * @property nextLocked - Whether the next arrow is gated (the chapter's
 *   quiz isn't cleared). Kept in lockstep with the reader footer's next
 *   button so the top bar can't bypass the exercise gate.
 */
type ChapterSwitcherProps = {
  label: string
  prevHref: string | null
  nextHref: string | null
  nextLocked: boolean
}

/**
 * Reader chapter switcher — prev arrow · "Ch X / Y" · next arrow. The
 * label is the current position in the course's flat chapter sequence;
 * the arrows navigate to the neighbour chapters (disabled at the ends).
 *
 * The label is intentionally not yet a click-to-jump control: a full
 * jump-to-chapter sheet needs a part chapter-list surface; prev/next
 * over `neighbors` is the shipped switcher for now.
 *
 * TODO(#940): full jump sheet needs a part chapter-list endpoint.
 *
 * @param props - {@link ChapterSwitcherProps}.
 * @returns The switcher element.
 */
function ChapterSwitcher(props: ChapterSwitcherProps): JSX.Element {
  const navigate = useNavigate()
  const { prevHref, nextHref } = props
  return (
    <div className={styles.switcher}>
      {prevHref && (
        <button
          type="button"
          className={styles.navArrow}
          onClick={() => navigate(prevHref)}
          aria-label="Previous chapter"
          title="Previous chapter">
          <ChevronRightIcon className={styles.arrowFlip} />
        </button>
      )}
      <span className={styles.switcherLabel}>{props.label}</span>
      {nextHref && (
        <button
          type="button"
          className={styles.navArrow}
          disabled={props.nextLocked}
          aria-disabled={props.nextLocked || undefined}
          onClick={() => {
            if (props.nextLocked) return
            navigate(nextHref)
          }}
          aria-label="Next chapter"
          title={
            props.nextLocked
              ? 'Pass the knowledge check to continue'
              : 'Next chapter'
          }>
          <ChevronRightIcon />
        </button>
      )}
    </div>
  )
}

/**
 * Props for {@link AvatarMenu}.
 *
 * @property learner - The signed-in learner; drives the avatar + header.
 * @property onOpenSettings - Open the Settings modal.
 */
type AvatarMenuProps = {
  learner: AccountLearner
  onOpenSettings: () => void
}

/**
 * Lab-local avatar dropdown — account header + the reader-specific menu
 * items the shared `AccountMenu` doesn't carry. Closes on outside-click
 * or Escape (mirrors `AccountMenu`'s dismissal contract).
 *
 *   - **Settings** → opens the {@link SettingsModal}.
 *   - **Sign out** → the existing signout mutation + bounce to
 *     `/authorize` (same chain as `site_chrome.tsx`'s `handleSignOut`).
 *
 * Conversion lives on the {@link BudgetMeter} pill (chrome) + the
 * out-of-time takeover, not as a menu item — the dropdown stays
 * account-management only.
 *
 * @param props - {@link AvatarMenuProps}.
 * @returns The avatar trigger + dropdown.
 */
function AvatarMenu(props: AvatarMenuProps): JSX.Element {
  const navigate = useNavigate()
  const signout = useSignoutMutation()
  const [open, setOpen] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    setImgFailed(false)
  }, [props.learner.avatarUrl])

  /** Open Settings; close the dropdown first. */
  function onSettings(): void {
    setOpen(false)
    props.onOpenSettings()
  }

  /** Sign out, then bounce to /authorize (mirrors site_chrome). */
  async function onSignOut(): Promise<void> {
    setOpen(false)
    try {
      await signout.mutateAsync()
    } finally {
      navigate('/authorize', { replace: true })
    }
  }

  const showAvatar = props.learner.avatarUrl != null && !imgFailed
  const initials = deriveInitials(props.learner)

  return (
    <div className={styles.avatarMenu} ref={rootRef}>
      <button
        className={styles.avatarTrigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen(o => !o)}>
        {showAvatar ? (
          <img
            className={styles.avatarImg}
            referrerPolicy="no-referrer"
            src={props.learner.avatarUrl!}
            alt=""
            onError={() => setImgFailed(true)}
          />
        ) : (
          initials
        )}
      </button>
      {open && (
        <div className={styles.menuPanel} role="menu">
          <div className={styles.menuHeader}>
            <p className={styles.menuName}>{props.learner.displayName}</p>
            <p className={styles.menuEmail}>{props.learner.email}</p>
          </div>
          <button
            className={styles.menuItem}
            type="button"
            role="menuitem"
            onClick={onSettings}>
            Settings
          </button>
          <button
            className={styles.menuItem}
            type="button"
            role="menuitem"
            onClick={() => void onSignOut()}>
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Snapshot the browser particulars attached to every issue report
 * (#1672). Read at submit time so `viewport` reflects the current window.
 * The daemon redacts `url` before forwarding.
 *
 * @returns The `client` block for the report request.
 */
function collectClient(): ReportRequest['client'] {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    url: window.location.href,
  }
}

/**
 * Pick a two-character initials string for the avatar fallback — word
 * initials from `displayName`, falling back to the email local-part.
 * Local copy of `AccountMenu`'s helper so the lab dropdown doesn't
 * depend on the shared component's internals.
 *
 * @param learner - The learner to derive initials for.
 * @returns Two-character upper-cased initials.
 */
function deriveInitials(learner: AccountLearner): string {
  const name = learner.displayName.trim()
  if (name.length > 0) {
    const words = name.split(/\s+/).filter(w => w.length > 0)
    if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase()
    if (words.length === 1) {
      const w = words[0]!
      return (w[0]! + (w[1] ?? '')).toUpperCase()
    }
  }
  const local = learner.email.split('@')[0] ?? ''
  return ((local[0] ?? '?') + (local[1] ?? '')).toUpperCase()
}
